import { inject, Injectable } from '@angular/core';
import { orderBy, round } from 'es-toolkit/compat';
import { forkJoin, Observable, of } from 'rxjs';

import { CsvAsStringArrays } from '@malou-io/package-utils';

import { LightRestaurant } from '#modules/restaurants/models/restaurant';
import { AggregatedWheelOfFortuneGiftsStatisticsData } from '#modules/statistics-boosters/models/aggregated-booster.interface';
import { BoostersAggregatedDataFetchingService } from '#modules/statistics-boosters/services/get-boosters-aggregated-data.service';
import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';
import { GIFT_PERCENTAGE_DECIMALS } from '#shared/constants/index';
import { ChartSortBy } from '#shared/enums/sort.enum';

interface Data {
    restaurants: LightRestaurant[];
    giftsData: AggregatedWheelOfFortuneGiftsStatisticsData;
}

interface Options {
    restaurantsWithBoosterPackActivated: LightRestaurant[];
}

@Injectable()
export class AggregatedBoostersGiftsCsvInsightService extends AbstractCsvService<Data, Options> {
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);
    private readonly _getBoostersAggregatedDataService = inject(BoostersAggregatedDataFetchingService);

    protected _getData$({ restaurantsWithBoosterPackActivated }: Options): Observable<Data> {
        return forkJoin({
            restaurants: of(restaurantsWithBoosterPackActivated),
            giftsData: this._getBoostersAggregatedDataService.getGiftsData(
                this._aggregatedStatisticsStore.filters().dates,
                restaurantsWithBoosterPackActivated.map((restaurant) => restaurant.id)
            ),
        });
    }

    protected override _isDataValid({ giftsData, restaurants }: Data): boolean {
        return giftsData.giftsInsightsPerRestaurant.length > 0 && restaurants.length > 0;
    }

    protected override _getCsvHeaderRow(): string[] {
        return [
            'Location',
            'Location Internal Name',
            'Location Address',
            'Winners Count',
            'Retrieved Gifts Count',
            'Retrieved Gifts Ratio',
        ];
    }

    protected override _getCsvDataRows({ restaurants, giftsData }: Data): CsvAsStringArrays {
        const { giftsInsightsPerRestaurant } = giftsData;
        return orderBy(restaurants, ['name'], [ChartSortBy.ASC]).map(({ id, name, internalName, address }) => {
            const restaurantsGiftsData = giftsInsightsPerRestaurant.find((giftStats) => giftStats.restaurant.id === id);
            return [
                name ?? '-',
                internalName ?? '-',
                address?.formattedAddress ?? address?.toString() ?? '-',
                restaurantsGiftsData?.giftDrawCount ? restaurantsGiftsData.giftDrawCount.toString() : '0',
                restaurantsGiftsData?.retrievedGiftDrawCount ? restaurantsGiftsData.retrievedGiftDrawCount.toString() : '0',
                restaurantsGiftsData?.retrievedGiftDrawCount
                    ? `${round(
                          (restaurantsGiftsData?.retrievedGiftDrawCount / restaurantsGiftsData?.giftDrawCount) * 100,
                          GIFT_PERCENTAGE_DECIMALS
                      )}%`
                    : '0%',
            ];
        });
        return [];
    }
}
